import { Button, Checkbox, Input, Label, Modal, Textarea } from "@devhop/ui";
import { TrashIcon } from "lucide-react";
import { nanoid } from "nanoid";
import { useEffect, useMemo, useState } from "react";
import { computeDocumentTotalsFromItems } from "@/modules/quotation-invoice/domain/compute-totals-from-items";
import type {
  ConvertQuotationToInvoiceBody,
  QuotationInvoiceDocumentItemDTO,
  QuotationInvoiceHeadroomDTO,
} from "@/modules/quotation-invoice/domain/types";
import { formatDocumentCurrency } from "../lib/format-document-currency";

const AMOUNT_EPS = 0.005;

type LineRow = {
  id: string;
  description: string;
  detail: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  discountAmount: number;
  productId: string | null;
  rawMaterialId: string | null;
};

export function ConvertQuotationToInvoiceDialog({
  open,
  onOpenChange,
  currency,
  headroom,
  initialItems,
  initialNotes,
  initialDiscount,
  initialTaxRate,
  submitting,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currency: string;
  headroom: QuotationInvoiceHeadroomDTO | null;
  initialItems: QuotationInvoiceDocumentItemDTO[];
  initialNotes: string | null;
  initialDiscount: number;
  initialTaxRate: number;
  submitting?: boolean;
  onConfirm: (body: ConvertQuotationToInvoiceBody) => void;
}) {
  const [lines, setLines] = useState<LineRow[]>([]);
  const [notes, setNotes] = useState("");
  const [discountAmount, setDiscountAmount] = useState(0);
  const [vatEnabled, setVatEnabled] = useState(true);
  const [taxRate, setTaxRate] = useState(0.1);

  useEffect(() => {
    if (!open) return;
    setLines(
      initialItems.map((item) => ({
        id: nanoid(),
        description: item.description,
        detail: item.detail ?? "",
        quantity: item.quantity,
        unit: item.unit ?? "",
        unitPrice: item.unitPrice,
        discountAmount: item.discountAmount ?? 0,
        productId: item.productId ?? null,
        rawMaterialId: item.rawMaterialId ?? null,
      })),
    );
    setNotes(initialNotes ?? "");
    setDiscountAmount(initialDiscount);
    const hasVat = initialTaxRate > 0;
    setVatEnabled(hasVat);
    setTaxRate(hasVat ? initialTaxRate : 0.1);
  }, [open, initialItems, initialNotes, initialDiscount, initialTaxRate]);

  const effectiveTaxRate = vatEnabled ? taxRate : 0;

  const computed = useMemo(() => {
    const items = lines.map((row) => ({
      description: row.description,
      detail: row.detail.trim() ? row.detail.trim() : null,
      quantity: row.quantity,
      unit: row.unit.trim() || null,
      unitPrice: row.unitPrice,
      discountAmount: row.discountAmount,
    }));
    return computeDocumentTotalsFromItems(
      items,
      discountAmount,
      effectiveTaxRate,
    );
  }, [lines, discountAmount, effectiveTaxRate]);

  const remaining = headroom?.remaining ?? 0;
  const overBudget = computed.total > remaining + AMOUNT_EPS;
  const canSubmit =
    !overBudget &&
    remaining > AMOUNT_EPS &&
    lines.some((r) => r.description.trim() && r.quantity > 0);

  const fmt = (n: number) => formatDocumentCurrency(n, currency);

  const updateLine = (id: string, patch: Partial<LineRow>) => {
    setLines((prev) =>
      prev.map((row) => (row.id === id ? { ...row, ...patch } : row)),
    );
  };

  const removeLine = (id: string) => {
    setLines((prev) =>
      prev.length <= 1 ? prev : prev.filter((r) => r.id !== id),
    );
  };

  const addLine = () => {
    setLines((prev) => [
      ...prev,
      {
        id: nanoid(),
        description: "",
        detail: "",
        quantity: 1,
        unit: "",
        unitPrice: 0,
        discountAmount: 0,
        productId: null,
        rawMaterialId: null,
      },
    ]);
  };

  const handleSubmit = () => {
    const valid = lines.filter((r) => r.description.trim() && r.quantity > 0);
    if (valid.length === 0 || overBudget || remaining <= AMOUNT_EPS) return;
    onConfirm({
      items: valid.map((r) => ({
        description: r.description.trim(),
        detail: r.detail.trim() ? r.detail.trim() : null,
        quantity: r.quantity,
        unit: r.unit.trim() || null,
        unitPrice: r.unitPrice,
        discountAmount: r.discountAmount,
        productId: r.productId,
        rawMaterialId: r.rawMaterialId,
      })),
      notes: notes.trim() || null,
      discountAmount,
      taxRate: effectiveTaxRate,
    });
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="ອອກໃບບິນງວດຈາກລາຄາ"
      size="lg"
      className="max-h-[92vh] w-[min(96vw,1600px)] max-w-[96vw] sm:max-h-[90vh]"
    >
      <div className="flex max-h-[min(88vh,calc(100vh-5rem))] min-h-0 flex-col gap-0">
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
          {headroom && (
            <div className="grid gap-2 rounded-lg border bg-muted/30 p-3 text-sm sm:grid-cols-3">
              <div>
                <span className="text-muted-foreground">ຍອດລາຄາ</span>
                <p className="font-medium tabular-nums">
                  {fmt(headroom.quotationTotal)}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">ຮວມໃບບິນແລ້ວ</span>
                <p className="font-medium tabular-nums">
                  {fmt(headroom.invoicedTotal)}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">ເຫຼືອສຳລັບງວດນີ້</span>
                <p
                  className={
                    remaining <= AMOUNT_EPS
                      ? "font-semibold text-destructive tabular-nums"
                      : "font-semibold text-primary tabular-nums"
                  }
                >
                  {fmt(remaining)}
                </p>
              </div>
            </div>
          )}

          {remaining <= AMOUNT_EPS && (
            <p className="text-destructive text-sm">
              ບໍ່ມີຍອດເຫຼືອຈາກລາຄາ — ບໍ່ສາມາດສ້າງໃບບິນງວດໃໝ່
            </p>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="pb-2 font-medium">ລາຍການ</th>
                  <th className="min-w-28 pb-2 font-medium">ລາຍລະອຽດ</th>
                  <th className="w-20 pb-2 text-right font-medium">ຈຳນວນ</th>
                  <th className="w-24 pb-2 text-right font-medium">ຫນ່ວຍ</th>
                  <th className="w-28 pb-2 text-right font-medium">ລາຄາ</th>
                  <th className="w-28 pb-2 text-right font-medium">ສ່ວນຫຼຸດ</th>
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody>
                {lines.map((row) => (
                  <tr key={row.id} className="border-b">
                    <td className="max-w-[min(100%,14rem)] py-2 pr-2">
                      <Input
                        value={row.description}
                        onChange={(e) =>
                          updateLine(row.id, { description: e.target.value })
                        }
                        placeholder="ລາຍການ"
                        className="h-9"
                      />
                    </td>
                    <td className="max-w-48 min-w-28 py-2 pr-2 align-top">
                      <Textarea
                        value={row.detail}
                        onChange={(e) =>
                          updateLine(row.id, { detail: e.target.value })
                        }
                        placeholder="ບໍ່ບັງຄັບ"
                        className="min-h-[48px] text-xs"
                        rows={2}
                      />
                    </td>
                    <td className="py-2 text-right">
                      <Input
                        type="number"
                        min={0}
                        step={1}
                        value={row.quantity || ""}
                        onChange={(e) =>
                          updateLine(row.id, {
                            quantity: Number(e.target.value) || 0,
                          })
                        }
                        className="h-9 w-20 text-right"
                      />
                    </td>
                    <td className="py-2 pr-2">
                      <Input
                        value={row.unit}
                        onChange={(e) =>
                          updateLine(row.id, { unit: e.target.value })
                        }
                        className="h-9 w-24"
                      />
                    </td>
                    <td className="py-2 text-right">
                      <Input
                        type="number"
                        min={0}
                        step={0.01}
                        value={row.unitPrice || ""}
                        onChange={(e) =>
                          updateLine(row.id, {
                            unitPrice: Number(e.target.value) || 0,
                          })
                        }
                        className="h-9 w-28 text-right"
                      />
                    </td>
                    <td className="py-2 text-right">
                      <Input
                        type="number"
                        min={0}
                        step={0.01}
                        value={row.discountAmount || ""}
                        onChange={(e) =>
                          updateLine(row.id, {
                            discountAmount: Number(e.target.value) || 0,
                          })
                        }
                        className="h-9 w-24 text-right"
                      />
                    </td>
                    <td className="py-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => removeLine(row.id)}
                        disabled={lines.length <= 1}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto px-0"
            onClick={addLine}
          >
            + ເພີ່ມລາຍການ
          </Button>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="conv-doc-discount">ສ່ວນຫຼຸດ (ເອກະສານ)</Label>
              <Input
                id="conv-doc-discount"
                type="number"
                min={0}
                step={0.01}
                value={discountAmount || ""}
                onChange={(e) => setDiscountAmount(Number(e.target.value) || 0)}
                className="mt-1"
              />
            </div>
            <div className="flex flex-col justify-end gap-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="conv-vat"
                  checked={vatEnabled}
                  onCheckedChange={(v) => setVatEnabled(Boolean(v))}
                />
                <label htmlFor="conv-vat" className="text-sm">
                  VAT
                </label>
              </div>
              <Input
                type="number"
                min={0}
                max={100}
                step={0.01}
                disabled={!vatEnabled}
                value={Math.round(taxRate * 10000) / 100}
                onChange={(e) =>
                  setTaxRate(
                    Math.min(
                      1,
                      Math.max(0, (Number(e.target.value) || 0) / 100),
                    ),
                  )
                }
                aria-label="VAT %"
              />
            </div>
          </div>

          <div className="flex flex-wrap justify-end gap-x-6 gap-y-1 border-t pt-3 text-sm">
            <span className="text-muted-foreground">ຍອດລວມງວດນີ້</span>
            <span className="font-semibold tabular-nums">
              {fmt(computed.total)}
            </span>
          </div>
          {overBudget && remaining > AMOUNT_EPS && (
            <p className="text-destructive text-sm">
              ຍອດງວດນີ້ ({fmt(computed.total)}) ເກີນຍອດເຫຼືອ ({fmt(remaining)})
            </p>
          )}

          <div>
            <Label htmlFor="conv-notes">ຫມາຍເຫດ (ໃບບິນງວດນີ້)</Label>
            <Textarea
              id="conv-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-1 min-h-[72px]"
              rows={3}
              placeholder="ສະເພາະໃບບິນງວດນີ້ — ບໍ່ກົງກັບຫມາຍເຫດໃນລາຄາ"
            />
          </div>
        </div>

        <div className="mt-4 flex shrink-0 justify-end gap-2 border-t pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            ຍົກເລີກ
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
          >
            {submitting ? "ກຳລັງສ້າງ..." : "ສ້າງໃບບິນຮ່າງ"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
